<template>
  <div class="accordion">
    <div
      v-for="panel, index of panels"
      :key="index"
      class="accordion__item"
      :class="[
        $blockClass(blockClass, 'accordion-item'),
      ]"
    >
      <button
        type="button"
        class="accordion__trigger"
        :class="[
          $blockClass(blockClass, 'trigger'),
          { [$class('active')]: isActive(index) }
        ]"
        :aria-expanded="isActive(index)"
        @click="togglePanel(index)"
      >
        <component
          :is="headingType"
          class="accordion__heading"
          :class="titleClass"
          v-text="panel.title"
        />

        <icon-chevron-down />
      </button>

      <transition-height>
        <div
          v-if="isActive(index)"
          class="accordion__content"
          v-html="panel.content"
        />
      </transition-height>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Accordion (accordion)
 * -----------------------------------------------------------------------------
 * Renders a set of accordion panels that can be opened and closed.
 *
 * @param {String} [blockClass] - Block class to apply to elements.
 * @param {String} headingType - Element type for the toggle label.
 * @param {Array} panelData - Array of panel objects.
 * @param {String} panelData.content - Panel content.
 * @param {String} panelData.title - Title of the panel, used as a label for the
 *   toggle button.
 * @param {String} [titleClass] - Space separated list of additional title
 * classes.
 */
import { mapState } from 'vuex'

import IconChevronDown from '~icons/directional-navigation/chevron/down.svg'
import TransitionHeight from '~async/transition-height/transition-height'

export default {
  name: 'AccordionSnippet',

  components: {
    IconChevronDown,
    TransitionHeight,
  },

  props: {
    blockClass: {
      type: String,
      default: '',
    },
    headingType: {
      type: String,
      default: 'span',
    },
    panelData: {
      type: Array,
      default: () => [],
    },
    singleToggle: {
      type: Boolean,
      default: false,
    },
    titleClass: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      panels: [],
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),
  },

  mounted() {
    this.setPanels()
  },

  methods: {

    /**
     * Check if a panel is active using its index.
     * @param {Number} index - Index of a specific panel.
     * @returns {Boolean}
     */
    isActive(index) {
      return this.panels[index].active
    },

    /**
     * Store panels on the component with a toggleable 'active' property.
     */
    setPanels() {
      this.panels = this.panelData.map((panel) => {
        return {
          active: false,
          ...panel,
        }
      })
    },

    /**
     * Toggle a panel's active state.
     * - On mobile viewports, panels are toggled independently.
     * - On non-mobile viewports, opening one panel will close all other panels.
     * @param {Number} targetPanel - Index of a specific panel.
     * @param {Boolean} singleToggle - True if only one is open at a time.
     */
    togglePanel(targetPanelIndex) {
      if (this.isMobile && !this.singleToggle) {
        const targetPanel = this.panels[targetPanelIndex]
        targetPanel.active = !targetPanel.active
        return
      }

      if (this.isActive(targetPanelIndex)) {
        this.panels[targetPanelIndex].active = false
        return
      }

      this.panels.forEach((panel, index) => {
        panel.active = targetPanelIndex === index
      })
    },
  },
}
</script>

<style lang="scss">
@import './accordion-snippet';
</style>
